type ChatMessage = {
  role: string;
  content: string;
  streaming?: boolean;
  streamId?: string;
};

let sequence = 0;

/** One stream owns one message. Completion changes its status, not its text. */
export function createStreamedReply<T extends ChatMessage>(
  setMessages: (update: (messages: T[]) => T[]) => void,
  create: (text: string) => T,
  matches?: (message: T) => boolean,
) {
  const streamId = `reply-stream-${++sequence}`;
  const template = create("");
  let receivedText = false;
  let closed = false;
  const update = (text: string) => {
    if (closed) return;
    if (!text) {
      setMessages((messages) => messages.filter((message) => message.streamId !== streamId));
      receivedText = false;
      return;
    }
    receivedText = true;
    setMessages((messages) => {
      const index = messages.findIndex((message) => message.streamId === streamId || matches?.(message));
      const next = { ...template, content: text, streamId, streaming: true };
      return index < 0 ? [...messages, next] : messages.map((message, i) => i === index ? next : message);
    });
  };
  return {
    update,
    finish(jsonFallback: string, extra?: Partial<T>) {
      if (closed) return;
      // JSON-only older servers remain supported. A streamed reply is never
      // replaced by the separately collected response returned by the request.
      if (!receivedText) update(jsonFallback);
      closed = true;
      setMessages((messages) => messages.map((message) =>
        message.streamId === streamId ? { ...message, ...extra, streaming: false } : message,
      ));
    },
    discard() {
      closed = true;
      setMessages((messages) => messages.filter((message) => message.streamId !== streamId));
    },
  };
}
